import { useEffect, useMemo, useState } from "react";
import Seo from "../components/Seo";
import MenuSection from "../components/MenuSection";
import { Reveal } from "../components/Reveal";
import { DRINK_CATEGORIES, MOCKTAIL_COCKTAIL_PAIRS } from "../data/drinks";
import { BASE_KEYWORDS, breadcrumbSchema, PAGE_KEYWORDS, RESPONSIBLE_MESSAGE } from "../data/brand";

export default function Drinks() {
  const [active, setActive] = useState(DRINK_CATEGORIES[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-25% 0px -65% 0px" }
    );
    DRINK_CATEGORIES.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const menuSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Menu",
      name: "Barverse Drinks Menu",
      hasMenuSection: DRINK_CATEGORIES.filter((c) => (c.items || []).length > 0).map((c) => ({
        "@type": "MenuSection",
        name: c.label,
        hasMenuItem: (c.groups ? c.groups.flatMap((g) => g.items) : c.items || []).map((it) => ({
          "@type": "MenuItem",
          name: it.name,
        })),
      })),
    }),
    []
  );

  return (
    <div className="pt-28 md:pt-36" data-testid="drinks-page">
      <Seo
        title="Cocktail Menu — Signature Cocktails, Margaritas, Craft Beer"
        description="The Barverse drinks menu — cocktails, draft and bottled beer, wine, tequila, vodka and zero-proof options. 21+ for alcoholic beverages."
        path="/drinks"
        jsonLd={[menuSchema, breadcrumbSchema("/drinks", "Drinks")]}
        keywords={`${BASE_KEYWORDS}, ${PAGE_KEYWORDS.drinks}`}
      />

      <header className="container-x">
        <Reveal>
          <p className="eyebrow">The Barverse menu</p>
          <h1 className="mt-5 font-display text-6xl uppercase leading-[0.9] text-bv-ink sm:text-7xl lg:text-8xl">
            Drinks
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bv-sub">
            Cocktails lead the program — signature mixes and core favorites up front, backed by draft
            and bottled beer, wine, tequila, spirits and zero-proof options. 21+ for alcoholic
            beverages; please drink responsibly.
          </p>
        </Reveal>
      </header>

      {/* Mobile category rail — sticky for one-hand menu scanning */}
      <div className="sticky top-16 z-30 mt-10 border-b border-bv-line bg-bv-base/95 py-3 backdrop-blur md:top-[72px] lg:hidden">
        <ul className="flex gap-2 overflow-x-auto px-5">
          {DRINK_CATEGORIES.map((c) => (
            <li key={c.id} className="shrink-0">
              <button
                type="button"
                onClick={() => goTo(c.id)}
                className={`chip !text-[10px] ${active === c.id ? "!border-bv-flame !text-bv-flame" : ""}`}
                data-testid={`drinks-chip-${c.id}`}
              >
                {c.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="container-x mt-12 grid gap-12 pb-24 lg:grid-cols-[240px_minmax(0,1fr)]">
        <nav className="hidden lg:block" aria-label="Drinks categories">
          <ul className="sticky top-32 space-y-1">
            {DRINK_CATEGORIES.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => goTo(c.id)}
                  className={`flex w-full items-baseline gap-3 rounded-lg px-4 py-2.5 text-left font-mono text-[11px] font-bold uppercase tracking-[0.18em] transition-colors duration-200 ${
                    active === c.id ? "bg-white text-bv-flame" : "text-bv-sub hover:text-bv-flame"
                  }`}
                  data-testid={`drinks-nav-${c.id}`}
                >
                  <span className="text-bv-faint">{String(i + 1).padStart(2, "0")}</span>
                  {c.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-16">
          {DRINK_CATEGORIES.map((c, i) => (
            <MenuSection
              key={c.id}
              index={i + 1}
              id={c.id}
              title={c.label}
              items={c.items}
              groups={c.groups}
              note={c.note}
            />
          ))}

          {/* Zero-proof connection — brief §14 */}
          <Reveal>
            <section className="rounded-2xl border border-bv-line bg-bv-panel p-8 md:p-12" data-testid="zero-proof-connection">
              <p className="eyebrow">Zero-proof connection</p>
              <h2 className="mt-4 font-display text-4xl uppercase leading-[0.95] text-bv-ink sm:text-5xl">
                Two verses. Same energy.
              </h2>
              <p className="mt-4 max-w-2xl leading-relaxed text-bv-sub">
                Each Tacoverse zero-proof favorite has a named Barverse counterpart — same
                flavor-forward thinking, one served zero proof, one spirited. The conversions stay
                behind the bar.
              </p>
              <ul className="mt-8 divide-y divide-bv-line/70">
                {MOCKTAIL_COCKTAIL_PAIRS.map((p) => (
                  <li
                    key={p.mocktail}
                    className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`pairing-${p.mocktail.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <img src="/images/tacosun.png" alt="" className="h-4 w-auto" aria-hidden="true" />
                      <span className="font-semibold text-bv-ink">{p.mocktail}</span>
                      <span className="hidden font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#B45309] md:inline">
                        Tacoverse™ Zero Proof
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="font-mono text-xs font-bold text-bv-flame" aria-hidden="true">→</span>
                      <span className="font-display text-xl uppercase text-bv-flame">{p.cocktail}</span>
                      <span className="hidden font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-bv-sub md:inline">
                        Barverse Cocktail
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>

          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-bv-faint">
            Menu availability varies by location. {RESPONSIBLE_MESSAGE}
          </p>
        </div>
      </div>
    </div>
  );
}
